/* eslint-disable no-undefined */
import { ExclamationOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { Spring } from '@react-spring/web';
import { Steps } from 'antd';
import ErrorSummary from 'common/publish-steps/containers/error-summary-container';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Sticker from 'react-stickyfill';
import { notification } from 'ui';
import {
  announcementBannerHeight,
  publishSteps as steps,
  validationTypes,
} from 'utils/app-configs';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import ErrorHandler from '../../error/components/error-handler';
import {
  AbortSpan,
  AbortText,
  ProcessInfo,
  StyledDiv,
  StyledDownIcon,
  StyledSpan,
  StyledSteps,
  StyledUpIcon,
  Text,
} from '../styles';
import WarningNotification from '../WarningNotification';
import { errorSummaryStyles } from './error-summary';

const { Step } = Steps;

export const showWarningNotification = (intl, onConfirm, isLoading) => {
  notification.warning({
    message: intl.formatMessage({ id: 'publishSteps.ABORT_TITLE' }),
    placement: 'top',
    description: <WarningNotification
      onConfirm={() => {
        onConfirm();
        notification.destroy('abortWarning');
      }}
      onClose={() => notification.destroy('abortWarning')}
      body={
        <>
          <Text>{intl.formatMessage({ id: 'publishSteps.ABORT_INFO' })}</Text>
          <Text marginTop={15}>
            {intl.formatMessage({ id: 'publishSteps.ABORT_INSTRUCTION' })}
          </Text>
        </>
      }
      isLoading={isLoading}
      confirmButtonText={intl.formatMessage({ id: 'publishSteps.ABORT' })}
      closeButtonText={intl.formatMessage({ id: 'common.CANCEL' })}
    />,
    key: 'abortWarning',
    className: 'extended-notification',
    duration: 0,
  });
};

class publishSteps extends Component {
  constructor(props) {
    super(props);
    this.state = {
      wasPolling: false,
      currentStatus: {},
      isOpen: true,
    };
  }

  componentDidMount() {
    this.checkPolling();
    this.timerHandle = undefined;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.currentStatus || !Object.keys(prevState.currentStatus).length) {
      return { currentStatus: nextProps.currentStatus };
    } else if (nextProps.currentStatus.status >= prevState.currentStatus.status) {
      return { currentStatus: nextProps.currentStatus };
    } else if (prevState.currentStatus.processInfo?.hasMultipleProcesses &&
      prevState.currentStatus.processInfo?.currentProcess <
      nextProps.currentStatus.processInfo?.currentProcess) {
      return { currentStatus: nextProps.currentStatus };
    } else if (
      // If the next status is smaller but the previous state had errors
      // or the previous status was done,
      // we take the next status as the new one.
      nextProps.currentStatus.status < prevState.currentStatus.status &&
      ((prevState.currentStatus.errors && prevState.currentStatus.errors.length) ||
        prevState.currentStatus.status === steps.done.status ||
        prevState.currentStatus.isTimedOut ||
        prevState.currentStatus.isStale)
    ) {
      return { currentStatus: nextProps.currentStatus };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    const { useSockets, isPolling } = this.props;
    const { currentStatus } = this.state;

    if (!prevProps.useSockets && !useSockets) {
      if (!prevProps.isPolling) {
        this.checkPolling();
        // Publish to SAP is Done!
      } else if (currentStatus && currentStatus.status === steps.done.status) {
        this.props.stopPublishStepPoll();
        this.props.reloadData();
        // When the status is 5 don't remove the step component immediately but wait 5 seconds
        this.timerHandle = setTimeout(() => {
          this.setState({ wasPolling: false, currentStatus: {} });
          this.timerHandle = undefined;
        }, 8000);
      }
    }

    if ((prevProps.useSockets && !useSockets) || (!prevProps.useSockets && useSockets)) {
      if (isPolling) {
        this.setState({ wasPolling: false, currentStatus: {} });
        this.stopPolling();
      }
      if (prevProps.useSockets && !useSockets) {
        this.checkPolling();
      }
    }

    if (this.props.abortFailure) {
      notification.info({
        message: this.props.intl.formatMessage({ id: 'publishSteps.ABORT_NOT_EXECUTED' }),
        placement: 'top',
        description: <WarningNotification
          onConfirm={() => {
            this.props.resetAbort();
            notification.destroy('abortInfo');
          }}
          body={
            <p>{this.props.intl.formatMessage({ id: 'publishSteps.ABORT_NOT_EXECUTED_INFO' })}</p>
          }
          isLoading={false}
          confirmButtonText={this.props.intl.formatMessage({ id: 'common.OK' })}
        />,
        key: 'abortInfo',
        className: 'extended-notification',
        duration: 0,
      });
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  stopPolling = () => {
    const { stopPublishStepPoll, useSockets } = this.props;
    if (!useSockets) {
      stopPublishStepPoll();
      if (this.timerHandle) {
        clearTimeout(this.timerHandle);
        this.timerHandle = undefined;
      }
    }
  };

  // eslint-disable-next-line complexity
  checkPolling = () => {
    const { startPublishStepPoll, id, type, isPolling, useSockets } = this.props;
    const { currentStatus } = this.state;

    if (!useSockets) {
      if (
        !isPolling &&
        currentStatus &&
        currentStatus.status > steps.unknown.status &&
        currentStatus.status < steps.done.status &&
        !currentStatus.isTimedOut &&
        !currentStatus.isStale &&
        (!currentStatus.errors || (currentStatus.errors && currentStatus.errors.length === 0))
      ) {
        startPublishStepPoll({ id, type });
        this.setState({ wasPolling: true });
        if (this.timerHandle) {
          clearTimeout(this.timerHandle);
          this.timerHandle = undefined;
        }
      } else if (
        !isPolling &&
        !this.timerHandle &&
        !isObjectNullOrEmpty(currentStatus) &&
        currentStatus?.status >= steps.done.status &&
        (
          !currentStatus?.isTimedOut &&
          !currentStatus?.isStale &&
          (!currentStatus?.errors || (currentStatus?.errors && currentStatus?.errors?.length === 0))
        )
      ) {
        this.timerHandle = setTimeout(() => {
          this.setState({ wasPolling: false, currentStatus: {} });
        }, 0);
      }
    }
  };

  toggleIsOpen = () => {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  renderShowMoreToggle = () => {
    const { isOpen } = this.state;
    const { isPolling, validationInfo } = this.props;
    const isWarning = validationInfo?.filter(v =>
      v.type === validationTypes.ConfirmationWarning,
    )?.length > 0;
    if (isPolling) {
      return null;
    }
    return (
      <StyledSpan onClick={this.toggleIsOpen} isWarning={isWarning}>
        {isOpen ?
          <FormattedMessage id='publishSteps.SHOW_LESS' />
          :
          <FormattedMessage id='publishSteps.SHOW_MORE' />
        }
        {isOpen ? <StyledUpIcon /> : <StyledDownIcon />}
      </StyledSpan>
    );
  };

  renderProcessInfo = () => {
    const { currentStatus } = this.state;
    return currentStatus?.processInfo?.hasMultipleProcesses ?
      <ProcessInfo>
        {/* eslint-disable-next-line */}
        {`(${currentStatus.processInfo.currentProcess}/${currentStatus.processInfo.totalProcesses}) ${currentStatus.processInfo.currentProcessDescription}`}
      </ProcessInfo> :
      null;
  };

  renderTitle = (step, stepStatus) => {
    const { intl, id, type, abortRequest, isAborting } = this.props;
    const { currentStatus } = this.state;
    const { errors, isTimedOut, isStale, isAbortable } = currentStatus;
    const hasErrors = errors?.length > 0 || isStale || isTimedOut;

    if (!hasErrors && isAbortable && stepStatus === 'process') {
      return <AbortSpan
        onClick={
          () => showWarningNotification(
            intl,
            () => abortRequest({ id, type, statusCode: step.status }),
            isAborting,
          )
        }
      >
        <span>{intl.formatMessage({ id: `publishSteps.${step.title}` })}</span>
        <AbortText>
          {intl.formatMessage({ id: 'publishSteps.ABORT' })}
        </AbortText>
      </AbortSpan>;
    }
    return intl.formatMessage({ id: `publishSteps.${step.title}` });
  };

  renderSteps = () => {
    const { currentStatus } = this.state;
    const { status, errors, isTimedOut, isStale } = currentStatus;
    const { validationInfo } = this.props;
    const warnings = validationInfo?.filter(v => v.type === validationTypes.ConfirmationWarning);

    return steps &&
      Object.values(steps) &&
      Object.values(steps)
        ?.filter(s => !s.hidden)
        // eslint-disable-next-line complexity
        ?.map((step, i) => {
          let stepStatus = 'wait';
          let renderShowMoreToggle = false;
          let icon = null;
          let className = 'line-unknown';
          const nextStep = steps && Object.values(steps) && Object.values(steps)[i + 2];

          if (step.status === status) {
            if ((errors && errors.length) || isTimedOut || isStale) {
              stepStatus = 'error';
              renderShowMoreToggle = true;
              icon = <ExclamationOutlined />;
            } else {
              stepStatus = 'process';
              icon = <LoadingOutlined />;
            }
          } else if (step.status < status) {
            if (step.status === 2 && status >= 3 && warnings?.length) {
              className = 'line-warning';
              stepStatus = 'finish';
            } else if (step.status === 3 && warnings?.length) {
              stepStatus = 'warning';
              renderShowMoreToggle = true;
              icon = <WarningOutlined />;
              className = 'line-finish';
            } else {
              stepStatus = 'finish';
              className = 'line-finish';
            }
          }

          if (
            nextStep &&
            nextStep.status === status &&
            stepStatus === 'finish' &&
            ((errors && errors.length > 0) || isTimedOut || isStale)
          ) {
            className = 'line-error';
          }

          const title = this.renderTitle(step, stepStatus);
          return (
            <Step
              title={title}
              key={step.status}
              description={renderShowMoreToggle && this.renderShowMoreToggle()}
              icon={icon}
              status={stepStatus}
              className={className}
            />
          );
        });
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      menuVisible,
      error,
      onDataReloaded,
      hasPlanningItems = false,
      validationInfo = [],
      isExternalCreate,
      isExternalUpdate,
      id,
      isGtsBlocked = false,
      hasAnnouncementBanner,
    } = this.props;
    const { currentStatus, isOpen } = this.state;
    const lockedOnDone = currentStatus?.status === steps.done.status &&
      !currentStatus?.errors?.length &&
      currentStatus?.actions?.length;
    const translateYpx = -106;
    const updatedTranslateY = hasAnnouncementBanner ?
      `${translateYpx + announcementBannerHeight}px` :
      `${translateYpx}px`;
    const originalTranslateY = hasAnnouncementBanner && window.pageYOffset > 10 ?
      `${announcementBannerHeight}px` :
      '0px';

    // send full object if you also need the types of the validations and handle it in error-summary
    const validationInfoLines = validationInfo?.length ?
      validationInfo?.filter(item =>
        item.type !== validationTypes.GtsBlock &&
        item.type !== validationTypes.ConfirmationWarning,
      )
        ?.reduce((acc, curr) => [...acc, ...curr.validations], []) : [];
    const validationWarningLines = validationInfo?.length ?
      validationInfo?.filter(item => item.type === validationTypes.ConfirmationWarning)
        ?.reduce((acc, curr) => [...acc, ...curr.validations], []) : [];
    const gtsBlockingError = validationInfo?.length ?
      validationInfo?.filter(item => item.type === validationTypes.GtsBlock)
        ?.reduce((acc, curr) => [...acc, ...curr.validations], []) : [];

    // If there is no operation info available,
    // don't render the publish steps
    if (
      (!currentStatus || !currentStatus.status) ||
      currentStatus.belongsToEntityId !== id
    ) {
      return null;
    }
    if (!validationWarningLines?.length &&
      (currentStatus.status === steps.draftValidationError.status ||
        currentStatus.status === steps.unknown.status ||
        (currentStatus.status >= steps.done.status && !this.state.wasPolling
        ))
    ) {
      if (
        validationInfoLines?.length ||
        gtsBlockingError?.length ||
        lockedOnDone
      ) {
        return (
          <ErrorSummary
            actions={currentStatus.actions}
            errors={isGtsBlocked ? gtsBlockingError : currentStatus.errors}
            isTimedOut={currentStatus.isTimedOut}
            isStale={currentStatus.isStale}
            status={currentStatus.status}
            timeoutInMinutes={currentStatus.timeoutInMinutes}
            isValidationError={1}
            onDataReloaded={onDataReloaded}
            hasPlanningItems={hasPlanningItems}
            info={validationInfoLines}
            warnings={validationWarningLines}
            isExternalCreate={isExternalCreate}
            isExternalUpdate={isExternalUpdate}
            isFromXML={!!validationInfo?.find(v => v.type === validationTypes.Xml)}
            isGtsBlocked={isGtsBlocked}
            extraStyles={errorSummaryStyles}
            isStandAlone={true}
            actionOnly={lockedOnDone}
          />
        );
      }
      return null;
    }

    return (
      <Sticker>
        <Spring
          to={{
            transform: menuVisible ?
              `translateY(${originalTranslateY})` :
              `translateY(${updatedTranslateY})`,
            boxShadow: menuVisible ? '0px 0px 0px 0px gray' : '0px 5px 20px -5px gray',
          }}
          config={{ duration: 400 }}
        >
          {props =>
            <StyledDiv
              bordered={
                currentStatus.isTimedOut ||
                (currentStatus.errors && Object.keys(currentStatus.errors).length) ||
                (
                  validationInfoLines?.length ||
                  gtsBlockingError?.length ||
                  validationWarningLines?.length
                )
              }
              hasErrors={
                currentStatus.isTimedOut ||
                (currentStatus.errors && Object.keys(currentStatus.errors).length)
              }
              hasWarnings={validationInfo?.filter(v =>
                v.type === validationTypes.ConfirmationWarning,
              )?.length > 0}
              style={props}
            >
              <ErrorHandler error={error} />
              {this.renderProcessInfo()}
              <StyledSteps>
                {this.renderSteps()}
              </StyledSteps>
              {isOpen &&
                <ErrorSummary
                  actions={currentStatus.actions}
                  errors={isGtsBlocked ? gtsBlockingError : currentStatus.errors}
                  isTimedOut={currentStatus.isTimedOut}
                  isStale={currentStatus.isStale}
                  status={currentStatus.status}
                  timeoutInMinutes={currentStatus.timeoutInMinutes}
                  onDataReloaded={onDataReloaded}
                  hasPlanningItems={hasPlanningItems}
                  renderShowMoreToggle={this.renderShowMoreToggle}
                  info={!isGtsBlocked ? validationInfoLines : []}
                  warnings={!isGtsBlocked ? validationWarningLines : []}
                  isExternalCreate={isExternalCreate}
                  isExternalUpdate={isExternalUpdate}
                  isFromXML={!!validationInfo?.find(v => v.type === validationTypes.Xml)}
                  isGtsBlocked={isGtsBlocked}
                  isStandAlone={false}
                />
              }
            </StyledDiv>
          }
        </Spring>
      </Sticker>
    );
  }
}

publishSteps.propTypes = {
  reloadData: PropTypes.func.isRequired,
  isPolling: PropTypes.bool,
  intl: PropTypes.object,
  startPublishStepPoll: PropTypes.func,
  stopPublishStepPoll: PropTypes.func,
  id: PropTypes.string,
  type: PropTypes.string,
  menuVisible: PropTypes.bool,
  error: PropTypes.object,
  onDataReloaded: PropTypes.func,
  abortRequest: PropTypes.func,
  isAborting: PropTypes.bool,
  abortFailure: PropTypes.bool,
  resetAbort: PropTypes.func,
  hasPlanningItems: PropTypes.bool,
  validationInfo: PropTypes.array,
  isExternalCreate: PropTypes.bool,
  isExternalUpdate: PropTypes.bool,
  isGtsBlocked: PropTypes.bool,
  hasAnnouncementBanner: PropTypes.bool,
  currentStatus: PropTypes.object,
  useSockets: PropTypes.bool,
};

export default injectIntl(publishSteps);
